import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BarChart3, Users, TrendingUp, Building2, FileText, Image as ImageIcon, BarChart4 } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { DataStore } from '@/lib/data-store';
import { CompanyStore, CompanyProfile } from '@/lib/company-store';
import { CompanyProfileCard } from '@/components/CompanyProfileCard';
import { CompanyProfileModal } from '@/components/CompanyProfileModal';

const Dashboard = () => {
  const [company, setCompany] = useState<CompanyProfile>(CompanyStore.getCompany());
  const [showProfileModal, setShowProfileModal] = useState(false);
  const data = DataStore.getData();
  
  // Calculate statistics
  const totalCustomers = data.customers.length;
  const activeCustomers = data.customers.filter((c: any) => c.status === 'Aktif').length;
  const totalInvoices = data.invoices.length;
  const totalRevenue = data.invoices.reduce((sum: number, inv: any) => sum + (inv.total || 0), 0);
  const totalCosts = data.costs.reduce((sum: number, cost: any) => sum + (cost.jumlah || 0), 0);
  const totalProfit = totalRevenue - totalCosts;
  const activeProjects = data.projects.filter((p: any) => p.status === 'Dalam Proses').length;

  const statsData = [
    { label: 'Jumlah Pendapatan', value: `RM ${totalRevenue.toFixed(2)}`, icon: TrendingUp, color: 'from-blue-500 to-cyan-500' },
    { label: 'Jumlah Kos', value: `RM ${totalCosts.toFixed(2)}`, icon: BarChart3, color: 'from-orange-500 to-red-500' },
    { label: 'Keuntungan Kasar', value: `RM ${totalProfit.toFixed(2)}`, icon: Building2, color: 'from-green-500 to-emerald-500' },
    { label: 'Projek Aktif', value: `${activeProjects} Aktif`, icon: BarChart4, color: 'from-purple-500 to-indigo-500' },
  ];

  // Pie chart data for project status
  const selesai = data.projects.filter((p: any) => p.status === 'Selesai').length;
  const dalamProses = data.projects.filter((p: any) => p.status === 'Dalam Proses').length;
  const tender = data.projects.filter((p: any) => p.status === 'Tender').length;
  const dibatalkan = data.projects.filter((p: any) => p.status === 'Dibatalkan').length;

  const projectStatusData = [
    { name: 'Selesai', value: selesai, color: '#10b981' },
    { name: 'Dalam Proses', value: dalamProses, color: '#3b82f6' },
    { name: 'Tender', value: tender, color: '#f59e0b' },
    { name: 'Dibatalkan', value: dibatalkan, color: '#ef4444' },
  ];

  const handleSaveProfile = (updatedCompany: CompanyProfile) => {
    setCompany(updatedCompany);
    setShowProfileModal(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Papan Pemuka</h1>
          <p className="text-slate-600 mt-1">Perkhidmatan SAP Ngecat</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-slate-500">{new Date().toLocaleDateString('ms-MY', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>
      </div>

      {/* Company Profile Card */}
      <CompanyProfileCard company={company} onEdit={() => setShowProfileModal(true)} />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statsData.map((stat, idx) => (
          <Card key={idx} className="border-l-4" style={{ borderLeftColor: stat.color.split(' ')[1].includes('blue') ? '#3b82f6' : stat.color.split(' ')[1].includes('orange') ? '#f97316' : stat.color.split(' ')[1].includes('green') ? '#10b981' : '#a855f7' }}>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-slate-600 text-sm font-medium">{stat.label}</p>
                  <p className="text-2xl font-bold text-slate-900 mt-2">{stat.value}</p>
                </div>
                <div className={`bg-gradient-to-br ${stat.color} p-3 rounded-lg`}>
                  <stat.icon className="text-white" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Grid - 2x2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Section 1: Pelanggan */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <span className="text-red-600 font-bold text-lg">1.</span>
              </div>
              <div>
                <CardTitle className="text-base">PELANGGAN</CardTitle>
                <CardDescription className="text-xs">SEBUTHARGA : INVOIS</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex gap-2">
                <Button size="sm" className="bg-red-600 hover:bg-red-700 flex-1">Pelanggan</Button>
                <Button size="sm" variant="outline" className="flex-1">Sebutharga</Button>
                <Button size="sm" variant="outline" className="flex-1">Invois</Button>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-slate-900">{activeCustomers}</p>
                <p className="text-xs text-slate-600">Pelanggan Aktif</p>
              </div>
              <Button size="sm" className="bg-red-600 hover:bg-red-700 w-full gap-2">
                <span>+</span> Pelanggan Baru
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Nilai Proyek & Kos */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <span className="text-red-600 font-bold text-lg">2.</span>
              </div>
              <div>
                <CardTitle className="text-base">NILAI PROJEK & KOS PROJEK</CardTitle>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-xs text-slate-600 mb-1">Jumlah Nilai Projek</p>
                <p className="text-2xl font-bold text-slate-900">RM {totalRevenue.toFixed(2)}</p>
                <p className="text-xs text-slate-600 mt-2">{totalInvoices} Invois</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-slate-600 mb-1">Jumlah Kos</p>
                  <p className="text-lg font-bold text-slate-900">RM {totalCosts.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-600 mb-1">Keuntungan Kasar</p>
                  <p className="text-lg font-bold text-green-600">RM {totalProfit.toFixed(2)}</p>
                  <p className="text-xs text-green-600">{totalRevenue > 0 ? ((totalProfit / totalRevenue) * 100).toFixed(1) : 0}%</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 3: Laporan Kerja */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <span className="text-red-600 font-bold text-lg">3.</span>
              </div>
              <div>
                <CardTitle className="text-base">LAPORAN KERJA</CardTitle>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex gap-2">
                <Button size="sm" className="bg-red-600 hover:bg-red-700">Semua</Button>
                <Button size="sm" variant="outline">Harian</Button>
                <Button size="sm" variant="outline">Mingguan</Button>
                <Button size="sm" variant="outline">Bulanan</Button>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                <p className="text-sm text-slate-600 text-center py-4">Tiada laporan kerja</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 4: Gambar Proyek */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <span className="text-red-600 font-bold text-lg">4.</span>
              </div>
              <div>
                <CardTitle className="text-base">GAMBAR PROJEK</CardTitle>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <select className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm">
                <option>Semua Proyek</option>
              </select>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-200 rounded-lg h-20 flex items-center justify-center text-xs text-slate-600">
                  Gambar 1
                </div>
                <div className="bg-slate-200 rounded-lg h-20 flex items-center justify-center text-xs text-slate-600">
                  Gambar 2
                </div>
              </div>
              <button className="text-red-600 hover:text-red-700 text-sm font-medium">Lihat Semua Gambar</button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Project Status Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Status Prestasi Proyek</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-8">
            <ResponsiveContainer width={200} height={200}>
              <PieChart>
                <Pie
                  data={projectStatusData}
                  cx={100}
                  cy={100}
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {projectStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-2">
              {projectStatusData.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                  <span className="text-sm text-slate-600">{item.name}: {item.value} ({Math.round((item.value / projectStatusData.reduce((sum, i) => sum + i.value, 0)) * 100)}%)</span>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Company Profile Modal */}
      {showProfileModal && (
        <CompanyProfileModal
          company={company}
          onClose={() => setShowProfileModal(false)}
          onSave={handleSaveProfile}
        />
      )}
    </div>
  );
};

export default Dashboard;